"use client";

import { usePathname } from "next/navigation";
import type { AppLocale } from "@/lib/i18n/constants";
import { buildLanguageSwitcherLinks, type LanguageSwitcherLabels } from "@/lib/i18n/language-switcher-links";

type LanguageSwitcherLinksProps = {
  currentLocale: AppLocale;
  label: string;
  labels: LanguageSwitcherLabels;
  visibleLocales: readonly AppLocale[];
};

export default function LanguageSwitcherLinks({ currentLocale, label, labels, visibleLocales }: LanguageSwitcherLinksProps) {
  const pathname = usePathname() ?? `/${currentLocale}`;
  const links = buildLanguageSwitcherLinks({
    pathname,
    currentLocale,
    visibleLocales,
    labels,
  });

  if (links.length <= 1) {
    return null;
  }

  return (
    <nav className="marketing-language-switcher" data-locale-switcher aria-label={label}>
      <span className="visually-hidden">{label}</span>
      <details className="marketing-language-switcher__details">
        <summary
          className="marketing-language-switcher__summary"
          aria-label={`${label}: ${labels[currentLocale] ?? currentLocale.toUpperCase()}`}
          title={labels[currentLocale] ?? currentLocale.toUpperCase()}
        >
          <span>{currentLocale.toUpperCase()}</span>
          <i className="bi bi-chevron-down" aria-hidden="true" />
        </summary>
        <ul className="marketing-language-switcher__list">
          {links.map((link) => (
            <li key={link.locale}>
              <a
                className={
                  link.current
                    ? "marketing-language-switcher__link marketing-language-switcher__link--active"
                    : "marketing-language-switcher__link"
                }
                href={link.href}
                hrefLang={link.locale}
                lang={link.locale}
                aria-current={link.current ? "page" : undefined}
                aria-label={link.label}
                title={link.label}
              >
                <span className="marketing-language-switcher__code">{link.locale.toUpperCase()}</span>
                <span className="marketing-language-switcher__name">{link.label}</span>
              </a>
            </li>
          ))}
        </ul>
      </details>
    </nav>
  );
}
